<template>
  <div>
    <HighChart :options="chartOptions" />
  </div>
</template>

<script>
import {Chart} from 'highcharts-vue'
import Highcharts from 'highcharts'
import moment from 'moment'

export default {
  name: 'DigitalSignalGraph',
  components: {
    HighChart: Chart
  },
  props: {
    minDate: {
      required: true
    },
    activeMeasurement: {
      required: false
    },
    sensors: {
      required: false
    },
    digitalSignals: {
      required: false,
      default: () => []
    },
    activeSignal: {
      required: false
    },
    chartMax: {
      required: true
    }
  },
  data () {
    return {
      chartOptions: this.createChartOptions(this),
      precedingTime: 3,
      succeedingTime: 3
    }
  },
  methods: {
    createChartOptions (t) {
      var activeSensors = this.sensors.filter(s => s.RailObjectId === this.activeMeasurement.RailObjectId)
      if (activeSensors.length === 0) {
        activeSensors = this.sensors
      }

      const severityIcon = ['', '', '⚠️', '🔺'][this.activeMeasurement.SeverityId]
      const dateTime = Highcharts.dateFormat('%e %b %Y %H:%M:%S', this.$options.filters.correctedMomentDate(this.activeMeasurement.MeasurementDateTime).toDate())
      const tempPerc = `[${this.activeMeasurement.Temperature}℃]`

      // const secondaryYAxis = [...new Set(activeSensors.map(s => s.ConfigSensor.UnitOfMeasure))].length > 1
      return {
        chart: {
          type: 'columnrange',
          inverted: true,
          zoomType: 'none',
          height: 200 + 10 * (this.digitalSignals.length),
          marginLeft: 120,
          marginRight: 70,
          // marginRight: secondaryYAxis ? 78 : undefined,
          stickyTracking: true,
          zooming: {
            mouseWheel: {
              enabled: false
            }
          }
        },
        exporting: {
          enabled: false
        },
        accessibility: {
          enabled: false
        },
        title: {
          text: ''
        },
        credits: {
          enabled: false
        },
        subtitle: {
          text: `${severityIcon} ${dateTime} ${this.directionString(this.activeMeasurement.Direction)} ${tempPerc} ${this.activeMeasurement.ObjectName || ''}`
        },
        lang: {
          noData: this.$t('charts.nodata')
        },
        legend: {
          enabled: false
        },
        plotOptions: {
          series: {
            states: {
              hover: {
                color: '#66F' // see charts.vue active signal. Use same value to match colors
              }
            },
            pointPadding: 0,
            groupPadding: 0.2,
            pointPlacement: -0.205,
            dataLabels: {
              enabled: true,
              inside: true
            },
            point: {
              events: {
                mouseOver: function (e) {
                  t.hoverPoint(this.low, this.high)
                },
                mouseOut: function (e) {
                  t.unhoverPoint()
                }
              }
            }
          },
          columnrange: {
            dataLabels: {
              enabled: false,
              align: 'right',
              format: ''
            }
          }
        },
        tooltip: {
          enabled: false
        },
        xAxis: {
          // sensors
          type: 'category',
          gridLineWidth: 1,
          gridLineColor: '#7cb5ec',
          gridZIndex: 4,
          labels: {
            enabled: true,
            align: 'left',
            x: -120,
            y: 10,
            style: {
              fontWeight: 'bold'
            }
          },
          categories: []
        },
        yAxis: {
          // time
          lineWidth: 1,
          gridLineWidth: 0,
          tickWidth: 1,
          title: {
            text: this.$t('views.switch.digital.time') + ' (s)'
          },
          min: 0,
          max: 0,
          startOnTick: false,
          endOnTick: false,
          tickInterval: 0.5
        },
        series: [
          {
            name: 'Digital Signals',
            data: []
          }
        ]
      }
    },
    setMinMaxTime: function (minTime, maxTime) {
      let min = minTime
      let max = Math.max(this.chartMax, maxTime)
      min = min - this.precedingTime
      max = max + this.succeedingTime

      this.chartOptions.yAxis.min = min
      this.chartOptions.yAxis.max = max
      if (this.chartMax < maxTime) {
        this.$emit('set-max', max)
      }
    },
    redraw () {
      let formattedData = []
      if (this.activeMeasurement) {
        let categories = []
        let index = 0
        const measurementTimeMs = moment(this.activeMeasurement.MeasurementDateTime).valueOf()
        this.digitalSignals.forEach((ds) => {
          categories.push(ds.EventCode)
          let times = []
          let on
          ds.Events.forEach((e) => {
            if (on === e.Value) {
              // ignore
            } else if (on !== undefined || e.Value) {
              let eventTimeMs = moment(e.MeasurementDateTime).valueOf()

              // push diff between start
              times.push((eventTimeMs - measurementTimeMs) / 1000)
              on = e.Value
            } else {
              // print the events from high to low as well
              times.push(-1 * this.precedingTime)
              let eventTimeMs = moment(e.MeasurementDateTime).valueOf()
              times.push((eventTimeMs - measurementTimeMs) / 1000)
            }
          })
          if (on) {
            times.push(this.chartMax + this.succeedingTime)
          }
          while (times.length >= 2) {
            formattedData.push({
              x: index,
              low: times[0],
              high: times[1]
            })
            times.splice(0, 2)
          }
          index++
        })
        this.chartOptions.series = [{
          name: 'Digital Signals',
          data: formattedData,
          turboThreshold: 1000
        }]
        const severityIcon = ['', '', '⚠️', '🔺'][this.activeMeasurement.SeverityId]
        const dateTime = Highcharts.dateFormat('%e %b %Y %H:%M:%S', this.$options.filters.correctedMomentDate(this.activeMeasurement.MeasurementDateTime).toDate())
        const tempPerc = `[${this.activeMeasurement.Temperature}℃]`
        this.chartOptions.subtitle.text = `${severityIcon} ${dateTime} ${this.directionString(this.activeMeasurement.Direction)} ${tempPerc} ${this.activeMeasurement.ObjectName || ''}`
        this.chartOptions.xAxis.categories = categories
        this.setMinMaxTime(0, (moment(this.activeMeasurement.MeasurementDateTimeEnd).valueOf() - measurementTimeMs) / 1000)
        this.chartOptions.chart.height = formattedData.length * 10 + 150
      }
    },
    hoverPoint (low, high) {
      this.$emit('hover-signal', {
        low: low,
        high: high
      })
    },
    unhoverPoint () {
      this.$emit('hover-signal', null)
    },
    directionString (dir) {
      const directions = [this.$t('charts.n'), this.$t('charts.r')]
      return directions[dir]
    }
  },
  watch: {
    digitalSignals () {
      this.redraw()
    },
    chartMax () {
      this.chartOptions.yAxis.max = this.chartMax
      this.redraw()
    },
    activeSignal (val) {
      //
    }
  },
  mounted () {
    this.redraw()
  }
}
</script>

<style scoped>
</style>



// WEBPACK FOOTER //
// src/components/switch/DigitalSignalGraph.vue